import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Banner, Box } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { getSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";
import { ActionButtons } from "app/pages/industry-analysis/players/table/bubble-chart/ActionButtons";
import { BubbleChartLoaderSkeleton } from "app/pages/industry-analysis/players/table/bubble-chart/BubbleChartLoaderSkeleton";
import { CHART_OPTIONS_META_DATA } from "app/pages/industry-analysis/players/table/bubble-chart/const";
import { CustomNoData } from "app/pages/industry-analysis/players/table/bubble-chart/CustomNoData";
import { Title } from "app/pages/industry-analysis/players/table/bubble-chart/Title";
import { ETypes } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import type { IRawData } from "app/pages/industry-analysis/players/table/types";
import { getConversionRateAvailability } from "app/pages/industry-analysis/overview/widgets/conversion-rate/shared/getConversionRateAvailability";
import { getIsPeriodOverPeriodChangeAvailable } from "app/pages/industry-analysis/players/utils/getIsPeriodOverPeriodChangeAvailable";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { BubbleChart } from "./BubbleChart";

export const isConversionRatesAvailableForBubbleChart = (rawData: IRawData | undefined): boolean =>
    rawData?.ConversionRatesAvailable === true;

const ORDERED_BUBBLE_CHART_TYPES: ETypes[] = [
    ETypes.AudienceGrowth,
    ETypes.ConversionRate,
    ETypes.BrandStrength,
    ETypes.CostEfficiency,
    ETypes.Loyalty,
];

const CONVERSION_RATE_DISPLAY_TYPE = ETypes[ETypes.ConversionRate];

export const getVisibleBubbleChartTypes = (showConversionBubbleTab: boolean): ETypes[] => {
    if (showConversionBubbleTab) {
        return ORDERED_BUBBLE_CHART_TYPES;
    }
    return ORDERED_BUBBLE_CHART_TYPES.filter((chartType) => chartType !== ETypes.ConversionRate);
};

export const resolveBubbleChartInitialType = (
    bubbleChartDisplayType: string | undefined,
    showConversionBubbleTab: boolean,
): ETypes => {
    const matchedChartType = ORDERED_BUBBLE_CHART_TYPES.find(
        (chartType) => ETypes[chartType] === bubbleChartDisplayType,
    );
    const resolved = matchedChartType ?? ETypes.AudienceGrowth;
    if (resolved === ETypes.ConversionRate && !showConversionBubbleTab) {
        return ETypes.AudienceGrowth;
    }
    return resolved;
};

const StyledInfoBanner = styled(Banner)`
    margin-top: 24px;
    margin-bottom: 24px;
`;

const StyledInfoBannerSubtitle = styled.span`
    ${setFontVariant("body1", colorsPalettes.carbon[400])}
`;

const NoComparisonDataInfoBanner = () => (
    <StyledInfoBanner
        title=""
        CustomIcon={
            <SWReactIcons
                iconName="info"
                size="xs"
            />
        }
        subtitle={
            <StyledInfoBannerSubtitle>
                {translate("market.research.players.bubble.banner.warning.text")}
            </StyledInfoBannerSubtitle>
        }
    />
);

const Container = styled(Box)`
    width: unset;
    height: auto;
`;

const ContentContainer = styled.div`
    height: 667px;
    box-sizing: border-box;
    padding: 0 24px 32px 24px;
`;

interface IBubbleChartWrapper {
    rawData: IRawData;
    isLoading: boolean;
}

export const BubbleChartWrapper: FC<IBubbleChartWrapper> = ({ rawData, isLoading }: IBubbleChartWrapper) => {
    const { params } = useCurrentStateAndParams();
    const selectedCategory = getSelectedCategory();
    const hasSegments = CategoryServiceUtils.hasSegments(selectedCategory);
    const { bubbleChartDisplayType, bubbleChartShowAverage } = params;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();

    const conversionRatesAvailable = isConversionRatesAvailableForBubbleChart(rawData);
    const isConversionRateEntitled = getConversionRateAvailability() === "available";
    const showConversionBubbleTab = isConversionRateEntitled && conversionRatesAvailable;

    const visibleChartTypes = useMemo(
        () => getVisibleBubbleChartTypes(showConversionBubbleTab),
        [showConversionBubbleTab],
    );

    const type = resolveBubbleChartInitialType(bubbleChartDisplayType, showConversionBubbleTab);

    const [showAverage, setShowAverage] = useState<boolean>(bubbleChartShowAverage === true.toString());

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (bubbleChartDisplayType === CONVERSION_RATE_DISPLAY_TYPE && !showConversionBubbleTab) {
            const resolved = resolveBubbleChartInitialType(bubbleChartDisplayType, showConversionBubbleTab);
            void proNavigator.applyUpdateParamsAsync({
                bubbleChartDisplayType: ETypes[resolved],
            });
        }
    }, [bubbleChartDisplayType, showConversionBubbleTab, isLoading]);

    const {
        xAxis,
        yAxis,
        zAxis,
        supportsIndustryWithSegments: selectedTabSupportsSegments = false,
    } = CHART_OPTIONS_META_DATA[type];

    const selectedTabRequiresPeriodOverPeriodChange = [xAxis.attribute, yAxis.attribute, zAxis.attribute].includes(
        "AdjacentPeriodChange",
    );
    const isPeriodOverPeriodChangeAvailable = getIsPeriodOverPeriodChangeAvailable();
    const isDatasetEmpty =
        !isLoading &&
        (!rawData ||
            !rawData.Data ||
            rawData.Data.length === 0 ||
            (selectedTabRequiresPeriodOverPeriodChange && !isPeriodOverPeriodChangeAvailable) ||
            (hasSegments && !selectedTabSupportsSegments));

    const isSelectedTabNotSupportedDoeToSegments = hasSegments && !selectedTabSupportsSegments;

    const hasIncompleteAudienceGrowthAdjacentPeriodData =
        isPeriodOverPeriod &&
        type === ETypes.AudienceGrowth &&
        rawData.CompareData?.every((item) => item[CHART_OPTIONS_META_DATA[type].yAxis.attribute] === undefined);

    const renderContent = () => {
        if (isLoading) {
            return <BubbleChartLoaderSkeleton />;
        }

        if (isDatasetEmpty) {
            return (
                <CustomNoData
                    type={type}
                    isPeriodOverPeriodChangeAvailable={isPeriodOverPeriodChangeAvailable}
                    selectedTabRequiresPeriodOverPeriodChange={selectedTabRequiresPeriodOverPeriodChange}
                    isSelectedTabNotSupportedDoeToSegments={isSelectedTabNotSupportedDoeToSegments}
                />
            );
        }

        return (
            <BubbleChart
                rawData={rawData}
                type={type}
                showAverage={showAverage}
            />
        );
    };

    return (
        <Container>
            <Title />
            <ContentContainer>
                <ActionButtons
                    type={type}
                    showAverage={showAverage}
                    setShowAverage={setShowAverage}
                    isDatasetEmpty={isDatasetEmpty}
                    visibleChartTypes={visibleChartTypes}
                />
                {hasIncompleteAudienceGrowthAdjacentPeriodData ? <NoComparisonDataInfoBanner /> : null}
                {renderContent()}
            </ContentContainer>
        </Container>
    );
};
